import React, { createContext, useState, useContext, useEffect } from 'react';
import AuthService from '../services/authService';
import { getUserFromToken } from '../utils/jwtDecoder';
import { isSubdomainStoreMode } from '../utils/storeUtils';
import { resolveLoginError } from '../utils/authErrorUtils';

const AuthContext = createContext(null);

// Helper to determine auth context from current URL path
const isCustomerPath = () => {
  if (isSubdomainStoreMode()) {
    return true;
  }

  return window.location.pathname.startsWith('/stores/');
};

export const AuthProvider = ({ children }) => {
  // Management auth state (AppAdmin, TeamMember)
  const [managementUser, setManagementUser] = useState(null);
  const [isManagementAuthenticated, setIsManagementAuthenticated] = useState(false);
  const [managementLoading, setManagementLoading] = useState(true);

  // Customer auth state
  const [customerUser, setCustomerUser] = useState(null);
  const [isCustomerAuthenticated, setIsCustomerAuthenticated] = useState(false);
  const [customerLoading, setCustomerLoading] = useState(true);

  useEffect(() => {
    // Check management auth from localStorage
    const managementToken = localStorage.getItem('management_auth_token');
    if (managementToken) {
      try {
        const userData = getUserFromToken(managementToken);
        if (userData) {
          setIsManagementAuthenticated(true);
          setManagementUser(userData);
        } else {
          localStorage.removeItem('management_auth_token');
          localStorage.removeItem('management_user_data');
        }
      } catch (error) {
        console.error('Invalid management token:', error);
        localStorage.removeItem('management_auth_token');
        localStorage.removeItem('management_user_data');
      }
    }
    setManagementLoading(false);

    // Check customer auth from localStorage
    const customerToken = localStorage.getItem('customer_auth_token');
    if (customerToken) {
      try {
        const userData = getUserFromToken(customerToken);
        if (userData) {
          setIsCustomerAuthenticated(true);
          setCustomerUser(userData);
        } else {
          localStorage.removeItem('customer_auth_token');
          localStorage.removeItem('customer_user_data');
        }
      } catch (error) {
        console.error('Invalid customer token:', error);
        localStorage.removeItem('customer_auth_token');
        localStorage.removeItem('customer_user_data');
      }
    }
    setCustomerLoading(false);
  }, []);

  // Login function for team member (management)
  const loginTeamMember = async ({ email, password }) => {
    try {
      const response = await AuthService.loginTeamMember(email, password);
      const token = response.data.accessToken;
      const userData = getUserFromToken(token);

      if (!userData) {
        throw new Error('Failed to decode user information from token');
      }

      localStorage.setItem('management_auth_token', token);
      localStorage.setItem('management_user_data', JSON.stringify(userData));
      if (userData.userType) {
        localStorage.setItem('last_user_type', userData.userType);
      }
      setIsManagementAuthenticated(true);
      setManagementUser(userData);
      return { success: true };
    } catch (error) {
      console.error('Team member login error:', error);
      return { success: false, ...resolveLoginError(error) };
    }
  };

  // Login function for app admin (management)
  const loginAppAdmin = async ({ email, password }) => {
    try {
      const response = await AuthService.loginAppAdmin(email, password);
      const token = response.data.accessToken;
      const userData = getUserFromToken(token);

      if (!userData) {
        throw new Error('Failed to decode user information from token');
      }

      localStorage.setItem('management_auth_token', token);
      localStorage.setItem('management_user_data', JSON.stringify(userData));
      if (userData.userType) {
        localStorage.setItem('last_user_type', userData.userType);
      }
      setIsManagementAuthenticated(true);
      setManagementUser(userData);
      return { success: true };
    } catch (error) {
      console.error('App admin login error:', error);
      return { success: false, ...resolveLoginError(error) };
    }
  };

  // Login function for customer
  const loginCustomer = async ({ email, password }) => {
    try {
      const response = await AuthService.loginCustomer(email, password);
      const token = response.data.accessToken;
      const userData = getUserFromToken(token);

      if (!userData) {
        throw new Error('Failed to decode user information from token');
      }

      localStorage.setItem('customer_auth_token', token);
      localStorage.setItem('customer_user_data', JSON.stringify(userData));
      if (userData.userType) {
        localStorage.setItem('last_user_type', userData.userType);
      }
      setIsCustomerAuthenticated(true);
      setCustomerUser(userData);
      return { success: true };
    } catch (error) {
      console.error('Customer login error:', error);
      const isNetworkError = !error.response ||
                             error.code === 'ECONNABORTED' ||
                             error.code === 'ERR_NETWORK' ||
                             error.message === 'Network Error' ||
                             (error.response && error.response.status >= 500);
      return { success: false, isNetworkError };
    }
  };

  // Login with Google (customer)
  const loginWithGoogle = async (idToken, language) => {
    try {
      const response = await AuthService.loginWithGoogle(idToken, language);
      const token = response.data.accessToken;
      const userData = getUserFromToken(token);

      if (!userData) {
        throw new Error('Failed to decode user information from token');
      }

      localStorage.setItem('customer_auth_token', token);
      if (response.data.refreshToken) {
        localStorage.setItem('customer_refresh_token', response.data.refreshToken);
      }
      localStorage.setItem('customer_user_data', JSON.stringify(userData));
      if (userData.userType) {
        localStorage.setItem('last_user_type', userData.userType);
      }
      setIsCustomerAuthenticated(true);
      setCustomerUser(userData);
      return true;
    } catch (error) {
      console.error('Google login error:', error);
      return false;
    }
  };

  // Register customer
  const registerCustomer = async (customerData) => {
    try {
      const response = await AuthService.registerCustomer(customerData);
      const token = response.data.accessToken;
      const userData = getUserFromToken(token);

      if (!userData) {
        throw new Error('Failed to decode user information from token');
      }

      localStorage.setItem('customer_auth_token', token);
      localStorage.setItem('customer_user_data', JSON.stringify(userData));
      if (userData.userType) {
        localStorage.setItem('last_user_type', userData.userType);
      }
      setIsCustomerAuthenticated(true);
      setCustomerUser(userData);
      return true;
    } catch (error) {
      console.error('Customer registration error:', error);
      return false;
    }
  };

  // Backward compatibility - defaults to team member login
  const login = loginTeamMember;

  const logoutManagement = () => {
    localStorage.removeItem('management_auth_token');
    localStorage.removeItem('management_refresh_token');
    localStorage.removeItem('management_user_data');
    setIsManagementAuthenticated(false);
    setManagementUser(null);
  };

  const logoutCustomer = () => {
    localStorage.removeItem('customer_auth_token');
    localStorage.removeItem('customer_refresh_token');
    localStorage.removeItem('customer_user_data');
    setIsCustomerAuthenticated(false);
    setCustomerUser(null);
  };

  return (
    <AuthContext.Provider value={{
      // Management auth
      managementUser,
      setManagementUser,
      isManagementAuthenticated,
      managementLoading,
      logoutManagement,
      // Customer auth
      customerUser,
      setCustomerUser,
      isCustomerAuthenticated,
      customerLoading,
      logoutCustomer,
      // Login functions
      login,
      loginTeamMember,
      loginAppAdmin,
      loginCustomer,
      loginWithGoogle,
      registerCustomer,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Path-aware hook: returns the active auth state based on current URL,
// plus explicit access to both management and customer states.
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  const customerContext = isCustomerPath();

  return {
    // Active state (resolved by current URL path)
    isAuthenticated: customerContext ? ctx.isCustomerAuthenticated : ctx.isManagementAuthenticated,
    user: customerContext ? ctx.customerUser : ctx.managementUser,
    loading: customerContext ? ctx.customerLoading : ctx.managementLoading,
    logout: customerContext ? ctx.logoutCustomer : ctx.logoutManagement,
    setUser: customerContext ? ctx.setCustomerUser : ctx.setManagementUser,

    // Login functions (always available)
    login: ctx.login,
    loginTeamMember: ctx.loginTeamMember,
    loginAppAdmin: ctx.loginAppAdmin,
    loginCustomer: ctx.loginCustomer,
    loginWithGoogle: ctx.loginWithGoogle,
    registerCustomer: ctx.registerCustomer,

    // Explicit management state
    managementUser: ctx.managementUser,
    setManagementUser: ctx.setManagementUser,
    isManagementAuthenticated: ctx.isManagementAuthenticated,
    managementLoading: ctx.managementLoading,
    logoutManagement: ctx.logoutManagement,

    // Explicit customer state
    customerUser: ctx.customerUser,
    setCustomerUser: ctx.setCustomerUser,
    isCustomerAuthenticated: ctx.isCustomerAuthenticated,
    customerLoading: ctx.customerLoading,
    logoutCustomer: ctx.logoutCustomer,
  };
}; 